import { useCallback, useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import { ReplaySearch } from "../../pages/messages/ReplaySearch";
import { requestRdbMessagesReply } from "../../api/Messages.api";
import { MessageDeliver, Messages, RdbMessageDeliverMapping } from "../../model/Message";
import { useDispatch } from "react-redux";
import { MessagingDeliveryDestinationController } from "../../controller/MessagingDeliveryDestinationController";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import { User } from "firebase/auth";
import { Spiner } from "../spinner/Spiner";
import TableList, { GridCol } from "../table/TableList";
import { ReplyButton } from "../../model/ReplyButton";

export type ReplyResultTallyProp = {
  currentUser: User | null;
  message: Messages;
  replyButtonSelected: ReplyButton;
  replyButtonNames: string[];
};

export const ReplyResultTally = ({
  currentUser,
  message,
  replyButtonSelected,
  replyButtonNames
}: ReplyResultTallyProp) => {
  const dispatch = useDispatch();

  const messageDeliveryDestination = new MessagingDeliveryDestinationController();

  const [isInitLoad, setIsInitLoad] = useState<boolean>(true);
  const [isLoad, setIsLoad] = useState<boolean>(true);

  const [messageDeliverList, setMessageDeliverList] = useState<MessageDeliver[]>([]);
  const [replyButtonCount, setReplyButtonCount] = useState<Map<string, number>>(new Map());
  const [unreplyNum, setUnreplyNum] = useState<number>(0);

  const [gridRowLength, setGridRowLength] = useState<number>(0);
  const [selectPage, setSelectPage] = useState<number>(1);
  const [selectNumber, setSelectNumber] = useState(50);
  const [firstPageFlg, setFirstPageFlg] = useState(false);
  const [searchCondition, setSearchCondition] = useState<any>({});

  const latestRequestId = useRef<number>(0);

  // 返信一覧を取得する共通処理
  useEffect(() => {
    const f = async () => {
      if (message) {
        const reqId = ++latestRequestId.current;
        const result = await requestRdbMessagesReply(
          currentUser!.tenantId ?? '',
          message.id,
          message.select ?? '',
          selectNumber.toString(),
          selectPage.toString(),
          searchCondition
        );
        if (reqId === latestRequestId.current) {
          // 最新のリクエストに対応する応答のみ処理する
          if (result.success) {
            const deliverList = result.data.rows.map((d: any) => RdbMessageDeliverMapping(d));
            setMessageDeliverList(deliverList);
            setReplyCount(result.data.count);
            setGridRowLength(result.data.total);
          } else {
            console.log(result.data)
            dispatch(alertOpen(AlertValue("返信一覧の取得に失敗しました。", "danger")));
          }
          setIsLoad(false);
          setIsInitLoad(false);
        }
      }
    }
    f();
  }, [message, selectPage, selectNumber, searchCondition]);

  // 返信ボタン結果集計
  function setReplyCount(replyData: [{count: string, reply_data: string}]) {
    // ボタン集計
    const replyButtonCount = new Map();
    replyButtonNames.forEach((name) => {
      const count = parseInt(replyData.find((rep) => rep.reply_data === name)?.count ?? '0');
      replyButtonCount.set(name, count);
    });
    setReplyButtonCount(replyButtonCount);

    // 未回答数
    const unreplyNum = parseInt(replyData.find((rep) => rep.reply_data === '未回答')?.count ?? '0');
    setUnreplyNum(unreplyNum);
  }

  const gridRow = useMemo(() => messageDeliverList.map((data: MessageDeliver) => {
    let replyState;
    let checkState;
    if (data.replyData !== '') {
      let displayName = data.replyData;
      if (message?.eventFlg && displayName === "会場で参加") {
        displayName = "会場";
      } else if (message?.eventFlg && displayName === "オンラインで参加") {
        displayName = "オンライン";
      }
      replyState = <span key={`badge-${data.userId}-${displayName}`}>{displayName}</span>;
    } else {
      replyState = <span key={`badge-${data.userId}-未回答`}>未回答</span>;
    }

    return {
      rowData: {
        userId: data.userId,
        checkbox: checkState,
        readed: data.readed === '' ? (<></>) : (data.readed === 'TRUE' ? <span className={"text-green-700 font-bold"}>既読</span> : <span className={"text-red-700 font-bold"}>未読</span>),
        name: `${data.lastName}`,
        account: data.account === '利用可' ? <span className={"text-green-700 font-bold"}>{data.account}</span> : <span className={"text-red-700 font-bold"}>{data.account}</span> ,
        reply: (<>{replyState}</>),
        replyData: data.replyData,
        group: data.groups,
      },
      sortId: data.userId || "", // ソート用のIDを追加
      style: `sm:whitespace-normal`
    };
  }), [message, messageDeliverList]);

  // 検索ボタンが押されたときの処理
  const handleSearch = (condition: any) => {
    setIsLoad(true);
    setSearchCondition(condition);
    setSelectPage(1);
    setFirstPageFlg(true);
  };

  // ページング処理
  const pagingFunc = useCallback((page: number) => {
    setFirstPageFlg(false);
    setSelectPage(page);
  }, []);

  // 返信ボタン結果集計のカラム
  let gridCol: GridCol[] = messageDeliveryDestination.replyGridCol;

  const selectNumberList =
    [
      { id: 50, name: "50" },
      { id: 100, name: "100" },
      { id: 200, name: "200" },
    ];

  return (
    <div>
      <div className="flex py-2">
        <div className="flex flex-1 flex-col pr-4">
          <div className="flex items-end">
            <h3 className="text-lg font-bold text-gray-900">■返信状況</h3>
          </div>
          <div className="flex justify-start">
            <ul className="ml-4 mt-1 flex">
              {replyButtonNames.map((name, index) => {
                const num = replyButtonCount.get(name);
                var displayName = name;
                if (message.eventFlg) {
                  if (name === "会場で参加") displayName = "会場";
                  if (name === "オンラインで参加") displayName = "オンライン";
                }
                return name !== undefined ? (
                  <li key={`${name}-${index}`} className="flex items-center">
                    <span>{displayName}</span>：<span>{num !== undefined ? num : ''}</span>名　
                  </li>
                ) : (
                  ""
                );
              })}
              <li className="flex items-center">
                <span>未回答</span>：<span>{unreplyNum}</span>名
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="pb-4">
        <ReplaySearch 
          messageId={message.id}
          replyButton={replyButtonSelected}
          onSearch={handleSearch}
        />
      </div>
      <div className="mb-5">
        <div className="relative">
          {isInitLoad ?
            <Spiner />
            :
            <TableList
              header
              footer={false}
              gridCol={gridCol}
              gridRow={gridRow}
              gridRowLength={gridRowLength}
              limit={selectNumber}
              selectNumberList={selectNumberList}
              setSelectNumber={setSelectNumber}
              pagingFunc={pagingFunc}
              firstPageFlg={firstPageFlg}
              setFirstPageFlg={setFirstPageFlg}
              minWidth={"min-w-[900px]"}
              fixedHeight={true}
            />
          }

          {isLoad && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
              <Spiner />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};